<!-- ─────────────────────────────────────────────────────────────────────── -->
<!-- Conversation Detail Sidebar                                             -->
<!-- Customer, orders, and products context for the active conversation.     -->
<!-- ─────────────────────────────────────────────────────────────────────── -->

<!-- No company linked: explain the value of linking, show what we know about the
     sender, and let the operator search and link a company without leaving the panel. -->
<ng-container *ngIf="!activeConversationDetails?.customer">
  <div class="conversation-detail__sidebar-section">
    <span class="conversation-detail__sidebar-title">{{ 'smart_inbox.inbox_view.sidebar.customer' | translate }}</span>

    <div class="sidebar-link-company__callout">
      <span class="sidebar-link-company__callout-icon">
        <ui-svg-icon svgIcon="company-cc" size="medium"></ui-svg-icon>
      </span>
      <h4 class="sidebar-link-company__callout-title">{{ 'smart_inbox.inbox_view.sidebar.no_company_linked' | translate }}</h4>
      <p class="sidebar-link-company__callout-text">{{ 'smart_inbox.inbox_view.sidebar.no_company_linked_description' | translate }}</p>
    </div>

    <!-- What we already know about the sender -->
    <div class="sidebar-link-company__sender" *ngIf="senderEmail">
      <h5 class="sidebar-link-company__sender-name" *ngIf="senderName">{{ senderName }}</h5>

      <div class="sidebar-link-company__sender-row">
        <span class="sidebar-link-company__sender-label">{{ 'smart_inbox.inbox_view.sidebar.email_label' | translate }}</span>
        <ui-copyable class="sidebar-link-company__sender-value" [value]="senderEmail" [label]="'smart_inbox.inbox_view.sidebar.email_label' | translate">
          <span class="sidebar-link-company__sender-text" [title]="senderEmail">{{ senderEmail }}</span>
        </ui-copyable>
      </div>

      <div class="sidebar-link-company__sender-row" *ngIf="senderDomain">
        <span class="sidebar-link-company__sender-label">{{ 'smart_inbox.inbox_view.sidebar.domain_label' | translate }}</span>
        <ui-copyable class="sidebar-link-company__sender-value" [value]="senderDomain" [label]="'smart_inbox.inbox_view.sidebar.domain_label' | translate">
          <span class="sidebar-link-company__sender-text" [title]="senderDomain">{{ senderDomain }}</span>
        </ui-copyable>
      </div>
    </div>

    <!-- Search and link -->
    <span class="conversation-detail__sidebar-title">{{ 'smart_inbox.inbox_view.sidebar.find_a_company' | translate }}</span>

    <!-- Opens the shared company picker (search + infinite pagination), the same
         one used by the conversation header, so linking behaves identically
         wherever it is started from. -->
    <button type="button"
            class="sidebar-link-company__search"
            #companySearchTrigger
            #companyMenuTrigger="matMenuTrigger"
            [matMenuTriggerFor]="sidebarCustomerMenu"
            (click)="onOpenCompanyMenu(companySearchTrigger)">
      <ui-svg-icon class="sidebar-link-company__search-icon" svgIcon="search" size="small"></ui-svg-icon>
      <span class="sidebar-link-company__search-placeholder">{{ 'smart_inbox.inbox_view.sidebar.find_a_company_placeholder' | translate }}</span>
    </button>

    <mat-menu #sidebarCustomerMenu="matMenu"
              class="inbox-menu inbox-view-menu company-picker-menu"
              [overlapTrigger]="true"
              (closed)="customerMenuIsOpen = false">
      <app-customer-association-menu
        *ngIf="customerMenuIsOpen"
        [selectedCustomerId]="null"
        [showRemove]="false"
        updateType="single"
        (customerSelected)="onLinkCompany($event)">
      </app-customer-association-menu>
    </mat-menu>
  </div>
</ng-container>

<!-- Customer associated -->
<ng-container *ngIf="activeConversationDetails?.customer && activeConversationThreadContext">
  <ng-container *ngIf="sidebarView === 'customer'">
    <!-- Customer -->
    <div class="conversation-detail__sidebar-section">
      <span class="conversation-detail__sidebar-title">{{ 'smart_inbox.inbox_view.sidebar.customer' | translate }}</span>

      <div class="conversation-detail__sidebar-field">
        <ui-svg-icon svgIcon="company-cc" size="medium"></ui-svg-icon>
        <div class="conversation-detail__sidebar-field-text">
          <span class="conversation-detail__sidebar-field-label">{{ 'smart_inbox.inbox_view.sidebar.company' | translate }}</span>
          <span class="conversation-detail__sidebar-field-value">{{ activeConversationThreadContext.company_name || '—' }}</span>
        </div>
      </div>

      <div class="conversation-detail__sidebar-field">
        <ui-svg-icon svgIcon="person" size="medium"></ui-svg-icon>
        <div class="conversation-detail__sidebar-field-text">
          <span class="conversation-detail__sidebar-field-label">{{ 'smart_inbox.inbox_view.sidebar.sender' | translate }}</span>
          <span class="conversation-detail__sidebar-field-value">{{ activeConversationThreadContext.sender.name || '—' }}</span>
          <span class="conversation-detail__sidebar-field-value-email">{{ activeConversationThreadContext.sender.email || '—' }}</span>
          <span class="conversation-detail__sidebar-field-value-contact" [class.is-known]="activeConversationThreadContext.sender.is_contact">
            <mat-icon>{{ activeConversationThreadContext.sender.is_contact ? 'check_circle_outline' : 'cancel_outline' }}</mat-icon>
            {{ (activeConversationThreadContext.sender.is_contact ? 'smart_inbox.inbox_view.sidebar.known_contact' : 'smart_inbox.inbox_view.sidebar.unknown_contact') | translate }}
          </span>
          <ui-button
            *ngIf="!activeConversationThreadContext.sender.is_contact && activeConversationThreadContext.sender.email"
            type="transparent"
            icon="plus-cc"
            iconSize="small"
            [label]="'smart_inbox.inbox_view.sidebar.add_contact' | translate"
            [disabled]="addingContact"
            [isLoading]="addingContact"
            (click)="addSenderToContacts()">
          </ui-button>
        </div>
      </div>

      <a class="conversation-detail__sidebar-link" (click)="viewCompany()">
        <span class="conversation-detail__sidebar-link-text">{{ 'smart_inbox.inbox_view.sidebar.view_company' | translate }}</span>
        <ui-svg-icon svgIcon="open-link" size="small"></ui-svg-icon>
      </a>
    </div>

    <!-- Orders -->
    <div class="conversation-detail__sidebar-section">
      <span class="conversation-detail__sidebar-title">{{ 'smart_inbox.inbox_view.sidebar.orders' | translate }}</span>

      <div class="conversation-detail__sidebar-stats">
        <div class="conversation-detail__sidebar-stat">
          <span class="conversation-detail__sidebar-stat-value">{{ activeConversationThreadContext.kpis.total_orders_amount | shortAmount: getCurrencySymbol() }}</span>
          <span class="conversation-detail__sidebar-stat-label">{{ 'smart_inbox.inbox_view.sidebar.total_orders' | translate }}</span>
        </div>
        <div class="conversation-detail__sidebar-stat">
          <span class="conversation-detail__sidebar-stat-value">{{ activeConversationThreadContext.kpis.average_order_value | shortAmount: getCurrencySymbol() }}</span>
          <span class="conversation-detail__sidebar-stat-label">{{ 'smart_inbox.inbox_view.sidebar.avg_value' | translate }}</span>
        </div>
        <div class="conversation-detail__sidebar-stat">
          <span class="conversation-detail__sidebar-stat-value">{{ activeConversationThreadContext.kpis.average_sku_count_per_order }}</span>
          <span class="conversation-detail__sidebar-stat-label">{{ 'smart_inbox.inbox_view.sidebar.avg_skus' | translate }}</span>
        </div>
      </div>

      <div class="conversation-detail__sidebar-order" *ngFor="let order of activeConversationThreadContext.last_orders" (click)="openOrderCorrectionPanel(order)">
        <div class="conversation-detail__sidebar-order-header">
          <span class="conversation-detail__sidebar-order-number">
            <ui-svg-icon svgIcon="file-cc" size="small"></ui-svg-icon>
            {{ order.name }}
          </span>
          <span class="conversation-detail__sidebar-order-status" [ngStyle]="getOrderStatusStyles(order.status)">{{ 'quotations_and_orders.QuotationStatus.' + order.status | translate }}</span>
        </div>
        <div class="conversation-detail__sidebar-order-details">
          <span class="conversation-detail__sidebar-order-date">{{ order.date | daysAgoOrLeft: 'ago' : 2 : 'DD/MM/YYYY' }}</span>
          <span class="conversation-detail__sidebar-order-amount">{{ order.total_amount | shortAmount: getCurrencySymbol() }}</span>
        </div>
      </div>

      <a class="conversation-detail__sidebar-action" (click)="viewAllOrders()">
        <span class="conversation-detail__sidebar-action-text">
          <ui-svg-icon svgIcon="quotations" size="small"></ui-svg-icon>
          {{ 'smart_inbox.inbox_view.sidebar.view_all_orders' | translate }}
        </span>
        <ui-svg-icon svgIcon="arrow-outward" size="xsmall"></ui-svg-icon>
      </a>
    </div>

    <!-- Products -->
    <div class="conversation-detail__sidebar-section">
      <span class="conversation-detail__sidebar-title">{{ 'smart_inbox.inbox_view.sidebar.products' | translate }}</span>

      <div class="conversation-detail__sidebar-stats">
        <div class="conversation-detail__sidebar-stat">
          <span class="conversation-detail__sidebar-stat-value">{{ activeConversationThreadContext.products_kpis.favorite_brand || '—' }}</span>
          <span class="conversation-detail__sidebar-stat-label">{{ 'smart_inbox.inbox_view.sidebar.fav_brand' | translate }}</span>
        </div>
        <div class="conversation-detail__sidebar-stat">
          <span class="conversation-detail__sidebar-stat-value">{{ activeConversationThreadContext.products_kpis.products_bought_count }}</span>
          <span class="conversation-detail__sidebar-stat-label">{{ 'smart_inbox.inbox_view.sidebar.products_bought' | translate }}</span>
        </div>
      </div>

      <a class="conversation-detail__sidebar-action" (click)="onSidebarViewChange('products')">
        <span class="conversation-detail__sidebar-action-text">
          <ui-svg-icon svgIcon="inventory" size="small"></ui-svg-icon>
          {{ 'smart_inbox.inbox_view.sidebar.check_products' | translate }}
        </span>
        <ui-svg-icon svgIcon="arrow-outward" size="xsmall"></ui-svg-icon>
      </a>
      <a class="conversation-detail__sidebar-action" (click)="onSidebarViewChange('catalogs')">
        <span class="conversation-detail__sidebar-action-text">
          <ui-svg-icon svgIcon="catalogs" size="small"></ui-svg-icon>
          {{ 'smart_inbox.inbox_view.sidebar.view_catalog' | translate }}
        </span>
        <ui-svg-icon svgIcon="arrow-outward" size="xsmall"></ui-svg-icon>
      </a>
    </div>
  </ng-container>

  <!-- Products or Catalogs: single list with scroll-based pagination -->
  <ng-container *ngIf="sidebarView === 'products' || sidebarView === 'catalogs'">
    <div class="conversation-detail__sidebar-section conversation-detail__sidebar-section--listings">
      <span class="conversation-detail__sidebar-title has-border-bottom">
        <ui-button type="transparent" icon="navigate-back-arrow" iconSize="medium" (click)="onSidebarViewChange('customer')">
        </ui-button>
        <span class="conversation-detail__sidebar-title-text">{{ 'smart_inbox.inbox_view.sidebar.back_to_customer' | translate }}</span>
      </span>

      <div class="conversation-detail__sidebar-search has-border-bottom">
        <ui-form-field
          [formControl]="searchInput"
          [prefixIcon]="'search_icon'"
          [suffixIconSize]="'small'"
          [placeholder]="(sidebarView === 'products' ? ('smart_inbox.inbox_view.sidebar.search_products_placeholder' | translate) : ('smart_inbox.inbox_view.sidebar.search_catalogs_placeholder' | translate))">
        </ui-form-field>
      </div>

      <span class="conversation-detail__sidebar-title conversation-detail__sidebar-title--list has-border-bottom">
        {{ (sidebarView === 'products' ? 'smart_inbox.inbox_view.sidebar.products_found' : 'smart_inbox.inbox_view.sidebar.catalogs_found') | translate:{ count: pagination?.count || 0 } }}
      </span>

      <div
        class="conversation-detail__sidebar-list"
        (scroll)="onListingsScroll($event)">
        <ng-container *ngFor="let item of listings; trackBy: trackByListingItem">
          <!-- Product card (IConversationCustomerProduct) -->
          <div
            *ngIf="item.kind === 'product'"
            class="conversation-detail__sidebar-product-card">
            <div class="conversation-detail__sidebar-product-card-name">{{ item.data.name }}</div>
            <div class="conversation-detail__sidebar-product-card-brand" *ngIf="item.data.brand">{{ item.data.brand }}</div>
            <div class="conversation-detail__sidebar-product-card-row">
              <span class="conversation-detail__sidebar-product-card-row-info">
                <span class="conversation-detail__sidebar-product-card-label">{{ 'smart_inbox.inbox_view.sidebar.sku' | translate }}</span>
                <span class="conversation-detail__sidebar-product-card-text">{{ item.data.sku || '—' }}</span>
              </span>
              <span class="conversation-detail__sidebar-product-card-row-info">
                <span class="conversation-detail__sidebar-product-card-label">{{ 'smart_inbox.inbox_view.sidebar.ean' | translate }}</span>
                <span class="conversation-detail__sidebar-product-card-text">{{ item.data.barcode || '—' }}</span>
              </span>
            </div>
            <div class="conversation-detail__sidebar-product-card-row">
              <span class="conversation-detail__sidebar-product-card-label">{{ 'smart_inbox.inbox_view.sidebar.msrp' | translate }}</span>
              <span class="conversation-detail__sidebar-product-card-text price-text">{{ item.data.sales_price != null && item.data.sales_price > 0 ? (item.data.sales_price | shortAmount: getCurrencySymbol()) : '—' }}</span>
            </div>
            <div class="conversation-detail__sidebar-product-card-row">
              <span class="conversation-detail__sidebar-product-card-label">{{ 'smart_inbox.inbox_view.sidebar.stock' | translate }}</span>
              <span class="conversation-detail__sidebar-product-card-stock" [ngClass]="getStockPillClass(item.data.available_quantity)">{{ item.data.available_quantity }} {{ 'smart_inbox.inbox_view.sidebar.units' | translate }}</span>
            </div>
            <div class="conversation-detail__sidebar-product-card-actions">
              <button type="button" class="conversation-detail__sidebar-product-card-btn" (click)="copyProduct(item.data)">
                <ui-svg-icon svgIcon="copy" size="small"></ui-svg-icon>
                {{ 'smart_inbox.inbox_view.sidebar.copy' | translate }}
              </button>
              <button type="button" class="conversation-detail__sidebar-product-card-btn" (click)="viewProductFull(item.data)">
                <ui-svg-icon svgIcon="open-link" size="small"></ui-svg-icon>
                {{ 'smart_inbox.inbox_view.sidebar.view_full' | translate }}
              </button>
            </div>
          </div>
          <!-- Catalog card (IConversationCustomerCatalog) -->
          <div
            *ngIf="item.kind === 'catalog'"
            class="conversation-detail__sidebar-catalog-card">
            <div class="conversation-detail__sidebar-catalog-card-content">
              <div class="conversation-detail__sidebar-catalog-card-image">
                <img *ngIf="item.data.image" [src]="item.data.image" [alt]="item.data.name" />
                <img *ngIf="!item.data.image" src="assets/icones/no-image.png" alt="" class="conversation-detail__sidebar-catalog-card-placeholder-img" />
              </div>
              <div class="conversation-detail__sidebar-catalog-card-body">
                <div class="conversation-detail__sidebar-catalog-card-name">{{ item.data.name }}</div>
                <div class="conversation-detail__sidebar-catalog-card-row">
                  <span class="conversation-detail__sidebar-catalog-card-label">{{ 'smart_inbox.inbox_view.sidebar.skus' | translate }}</span>
                  <span class="conversation-detail__sidebar-catalog-card-value">{{ item.data.nbr_products }}</span>
                </div>
              </div>
            </div>
            <div class="conversation-detail__sidebar-catalog-card-actions">
              <button type="button" class="conversation-detail__sidebar-catalog-card-btn" (click)="downloadCatalog(item.data)">
                <ui-svg-icon svgIcon="download" size="small"></ui-svg-icon>
                {{ 'smart_inbox.inbox_view.sidebar.download' | translate }}
              </button>
              <button type="button" class="conversation-detail__sidebar-catalog-card-btn" (click)="viewCatalogFull(item.data)">
                <ui-svg-icon svgIcon="open-link" size="small"></ui-svg-icon>
                {{ 'smart_inbox.inbox_view.sidebar.view_full' | translate }}
              </button>
            </div>
          </div>
        </ng-container>
        <div *ngIf="loading" class="conversation-detail__sidebar-list-loading">
          <ui-svg-icon svgIcon="refresh-cc" size="small" class="spin-animation"></ui-svg-icon>
          <span>{{ 'smart_inbox.inbox_view.sidebar.loading' | translate }}</span>
        </div>
        <div *ngIf="hasMoreToLoad && !loading" class="conversation-detail__sidebar-list-more">{{ 'smart_inbox.inbox_view.sidebar.scroll_for_more' | translate }}</div>
        <div *ngIf="listings.length === 0 && !loading" class="conversation-detail__sidebar-list-empty">
          {{ (sidebarView === 'products' ? 'smart_inbox.inbox_view.sidebar.no_products_found' : 'smart_inbox.inbox_view.sidebar.no_catalogs_found') | translate }}
        </div>
      </div>
    </div>
  </ng-container>
</ng-container>
